/**
 * Which session the chat is on: the reset when the user or the workspace
 * changes, persisting the id, the legacy storage cleanup, and the two ways to
 * change session by hand. Split out of `useAgentChat.ts`.
 *
 * The reset is an effect over the (user, workspace) scope rather than a `key=`
 * remount like App.tsx's sign-in-as-someone-else handling: keying the chat
 * provider would also remount the Layout around it.
 */
import { useCallback, useEffect, useRef } from 'react';

import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { cleanupLegacyStorage, loadStoredSession, storeSession } from './agentChatSessionStorage';
import { generateId, type ChatCore } from './useAgentChatCore';
import type { ChatSessionCreationSource } from './useAgentChat.types';

/**
 * Told which conversation New or History is leaving while its turn is live,
 * before the turn refs clear. Not the workspace or account reset: every way
 * back INTO a workspace clears the whole query cache (switchWorkspace's
 * `clearWorkspaceQueries`, the cross-tab switch, the auth change).
 * createWorkspace leaves without clearing, so a new `setWorkspace` path back
 * would need this mark there too.
 */
export type LeaveLiveTurn = (workspaceId: string | null, sessionId: string) => void;

function leaving(
  chat: ChatCore,
  onLeaveLiveTurn: LeaveLiveTurn | undefined,
  workspaceId: string | null,
  sessionId: string,
): void {
  if (chat.currentAssistantIdRef.current !== null) onLeaveLiveTurn?.(workspaceId, sessionId);
}

export interface ChatSessionLifecycle {
  readonly switchSession: (id: string) => void;
  readonly createNewSession: (trigger?: ChatSessionCreationSource) => void;
}

/**
 * The effects here run in this order, and the reset must come first: it clears
 * the turn refs and picks the new scope's session before the persistence
 * effect writes it and before the history seed reads it.
 */
export function useAgentChatSessionLifecycle(
  chat: ChatCore,
  workspaceId: string | null,
  userId: string | null,
  sessionId: string,
  onLeaveLiveTurn?: LeaveLiveTurn,
): ChatSessionLifecycle {
  // The session belongs to a user in a workspace; changing either is a switch.
  const scope = `${userId ?? ''}|${workspaceId ?? ''}`;
  const prevScopeRef = useRef(scope);

  // Reset chat state when the user switches workspaces, or another account
  // signs in to this one
  useEffect(() => {
    if (prevScopeRef.current === scope) return;
    prevScopeRef.current = scope;

    // User or workspace changed: abort the stream, clear state, load that scope's session
    chat.abortRef.current?.abort();
    chat.currentAssistantIdRef.current = null;
    chat.unacknowledgedRowIdsRef.current.clear();
    chat.currentTurnRowIdsRef.current = [];
    chat.setIsStreaming(false);
    chat.setCurrentAgent(null);
    chat.setMessages([]);
    chat.setRefusedDraft(null);
    // A refusal's pending re-seed belongs to the conversation being left.
    chat.refusalPendingRef.current = false;
    chat.stoppedOwnTurnRef.current = null;
    chat.stoppedReplyRef.current = null;
    chat.seededSessionRef.current = null;

    const stored = loadStoredSession(userId, workspaceId);
    if (stored) {
      chat.setSessionId(stored);
      return;
    }
    // Same client-minted case as createNewSession — no server row exists yet,
    // so mark it seeded and skip the certain-404 fetch here too. (Fixing only
    // the createNewSession path would leave this sibling with the same
    // wipe-on-404 window.)
    const freshId = generateId();
    chat.seededSessionRef.current = freshId;
    chat.clientMintedRef.current.add(freshId);
    chat.setSessionId(freshId);
  }, [chat, scope, userId, workspaceId]);

  // Persist session ID to localStorage whenever it changes
  useEffect(() => {
    storeSession(userId, workspaceId, sessionId);
  }, [userId, workspaceId, sessionId]);

  // Legacy ``localStorage`` message buckets pre-date the server-side
  // session history. Wipe them once per workspace switch.
  useEffect(() => {
    if (workspaceId) cleanupLegacyStorage(workspaceId);
  }, [workspaceId]);

  const switchSession = useCallback(
    (id: string) => {
      if (id === sessionId) return;
      leaving(chat, onLeaveLiveTurn, workspaceId, sessionId);
      chat.abortRef.current?.abort();
      // The abandoned turn belongs to the conversation being LEFT. Leaving the
      // ref set makes the seed in `useAgentChatHistory` read the next
      // conversation's transcript as arriving mid-turn, and paste this one's
      // rows underneath it. `cancelMessage` and `createNewSession` both clear
      // it; this was the one that did not.
      chat.currentAssistantIdRef.current = null;
      chat.unacknowledgedRowIdsRef.current.clear();
      chat.currentTurnRowIdsRef.current = [];
      // And CLEAR, for the same reason the workspace-switch effect does. This
      // leaned on the seed effect landing and overwriting — safe only while
      // every seed path wrote. Now that a failed read declines to, leaning on
      // it leaves the PREVIOUS conversation on screen under the new session's
      // id with nothing to say so: one thread's content under another's name,
      // the sharpest form of #887.
      chat.setMessages([]);
      // Nothing is seeded now, so nothing may count as seeded: back on a
      // conversation before the one between it landed its read, a leftover
      // stamp would skip the seed and leave the transcript empty.
      chat.seededSessionRef.current = null;
      // A message refused in the conversation being left is not this one's.
      chat.setRefusedDraft(null);
      // So does a refusal's pending re-seed: carried over, it would make this
      // session's own finished turn re-seed over its streamed reply.
      chat.refusalPendingRef.current = false;
      chat.stoppedOwnTurnRef.current = null;
      chat.stoppedReplyRef.current = null;
      chat.setIsStreaming(false);
      chat.setCurrentAgent(null);
      chat.setSessionId(id);
    },
    [chat, onLeaveLiveTurn, workspaceId, sessionId],
  );

  const createNewSession = useCallback(
    (trigger: ChatSessionCreationSource = 'new_chat') => {
      leaving(chat, onLeaveLiveTurn, workspaceId, sessionId);
      // Abort any in-flight stream
      chat.abortRef.current?.abort();
      chat.currentAssistantIdRef.current = null;
      chat.unacknowledgedRowIdsRef.current.clear();
      chat.currentTurnRowIdsRef.current = [];

      // Client-generated id, no server round-trip. The agent service creates
      // the row lazily on the first message (``_ensure_session``), so a "New
      // chat" the user never types into leaves nothing behind. Eagerly POSTing
      // /api/sessions used to persist an empty row that showed in the history
      // list forever as an untitled "Conversation" — ~40% of created sessions.
      const newId = generateId();
      // Mark it seeded BEFORE the id changes. The server has never seen this id,
      // so the history query is a guaranteed 404 — and the seed effect treats
      // 404 the same as an empty history, calling setMessages([]). The composer
      // is live while that request is in flight (sendMessage doesn't gate on
      // isLoadingHistory), so without this the first message of a brand-new chat
      // can be silently wiped when the 404 lands. Nothing to seed from a
      // client-minted id anyway.
      chat.seededSessionRef.current = newId;
      chat.clientMintedRef.current.add(newId);
      chat.setSessionId(newId);

      // Tracked AFTER minting so the event carries the id. Without it this event
      // counts intent with no way to join it to CHAT_MESSAGE_SENT (which does
      // carry session_id) — and since the row is now created lazily, "new chats
      // never typed into" is exactly the rate worth measuring.
      safeAnalytics.track(ANALYTICS_EVENTS.CHAT_SESSION_CREATED, {
        context_type: 'none',
        session_id: newId,
        creation_source: trigger,
      });

      chat.setMessages([]);
      chat.setRefusedDraft(null);
      // So does a refusal's pending re-seed: carried over, it would make this
      // session's own finished turn re-seed over its streamed reply.
      chat.refusalPendingRef.current = false;
      chat.stoppedOwnTurnRef.current = null;
      chat.stoppedReplyRef.current = null;
      chat.setIsStreaming(false);
      chat.setCurrentAgent(null);
    },
    [chat, onLeaveLiveTurn, workspaceId, sessionId],
  );

  return { switchSession, createNewSession };
}
